/**
 * Nguồn ảnh theo bậc của Bộ soi ảnh (spec 2026-10-28-bo-soi-anh-chuyen-nghiep, CEO chốt 1b · 2a).
 *
 * Mỗi ảnh có nhiều bản: bản nhỏ cho ô lưới, bản vừa, bản lớn 1600px, và tệp gốc nặng hàng chục MB.
 * Bày bản 768px vào khung cao 1.500 điểm là nhoè (đúng ảnh CEO gửi 28/10/2026); luôn tải tệp gốc
 * thì nặng. Thang bậc cho bộ soi chọn bản NHỎ NHẤT đủ nét cho khung đang hiện, và chỉ leo lên tệp
 * gốc khi người xem phóng quá độ nét của bản lớn.
 *
 * Tệp thuần, không React: mọi con số kiểm được bằng bài kiểm.
 */

export type Rung = {
  url: string;
  /** Cạnh dài (điểm ảnh). Tệp gốc chưa rõ kích thước = Infinity: coi là nét hơn mọi bậc. */
  longEdge: number;
  /** Nhãn cho dòng báo ("bản 1600px", "bản gốc"). */
  label: string;
  isOriginal?: boolean;
};

export type PhotoLadder = {
  /** Xếp từ nhẹ tới nặng. */
  rungs: Rung[];
  /** Kích thước tệp gốc — mốc của "100%". Vắng = lấy theo bậc lớn nhất đã tải. */
  natural?: { w: number; h: number } | undefined;
};

type GalleryUrls = {
  thumb?: string | undefined;
  medium?: string | undefined;
  large?: string | undefined;
  original?: string | undefined;
  width?: number | undefined;
  height?: number | undefined;
};

/** Thang bậc của một phiên bản kho album (link ký `thumb/medium/large/original`). */
export function ladderFromGallery(u: GalleryUrls | undefined): PhotoLadder {
  if (!u) return { rungs: [] };
  const natural = u.width && u.height && u.width > 0 && u.height > 0 ? { w: u.width, h: u.height } : undefined;
  const rungs: Rung[] = [];
  if (u.thumb) rungs.push({ url: u.thumb, longEdge: 256, label: "bản 256px" });
  if (u.medium) rungs.push({ url: u.medium, longEdge: 768, label: "bản 768px" });
  if (u.large) rungs.push({ url: u.large, longEdge: 1600, label: "bản 1600px" });
  if (u.original) {
    rungs.push({
      url: u.original,
      longEdge: natural ? Math.max(natural.w, natural.h) : Number.POSITIVE_INFINITY,
      label: "bản gốc",
      isOriginal: true,
    });
  }
  return { rungs, natural };
}

/**
 * Thang bậc từ MỘT link xem (thường là bản 1600px của kho chung) cộng link tệp gốc nếu màn có.
 * Cạnh dài bản xem chưa biết trước: 1600 là cỡ lớn nhất kho chung sinh ra.
 */
export function ladderFromUrls(url: string, originalUrl?: string): PhotoLadder {
  if (!url) return { rungs: [] };
  const rungs: Rung[] = [{ url, longEdge: 1600, label: "bản xem" }];
  if (originalUrl && originalUrl !== url) {
    rungs.push({ url: originalUrl, longEdge: Number.POSITIVE_INFINITY, label: "bản gốc", isOriginal: true });
  }
  return { rungs };
}

/** Bậc nhỏ nhất có cạnh dài ≥ nhu cầu; không bậc nào đủ thì bậc nét nhất. */
export function pickRung(ladder: PhotoLadder, needed: number): Rung | undefined {
  return ladder.rungs.find((r) => r.longEdge >= needed) ?? ladder.rungs.at(-1);
}

/**
 * Cần bậc có cạnh dài bao nhiêu điểm ảnh để không nhoè.
 *
 * - "fit": ảnh vừa khung ⇒ cạnh dài khung × độ nét màn hình (màn Retina 2×).
 * - số: tỉ lệ so với TỆP GỐC ⇒ cạnh dài gốc × tỉ lệ × độ nét. Chưa biết kích thước gốc mà đã
 *   phóng ⇒ xin bậc nét nhất.
 */
export function neededLongEdge(
  box: { w: number; h: number },
  natural: { w: number; h: number } | undefined,
  scale: number | "fit",
  dpr: number,
): number {
  const d = dpr > 0 ? dpr : 1;
  if (scale === "fit") return Math.ceil(Math.max(box.w, box.h) * d);
  if (!natural) return Number.POSITIVE_INFINITY;
  return Math.ceil(Math.max(natural.w, natural.h) * scale * d);
}
